import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../../widgets/Header';
import EveryBodyWinsList from '../../widgets/EveryBodyWinsList';
import { downloadImage } from '../../../utils/downloadImage';

/**
 * Everybody Wins — player unveil screen.
 * Expects location.state = { players: [{tier, image, name?}, ...], participantName, campaignName }
 */

const buildShareText = (playerName, participantName, campaignName, imageUrl) => {
  const campaign = campaignName || 'the campaign';
  const who = participantName ? `${participantName} just` : 'I just';
  const base =
    `🎉 ${who} unveiled ${playerName} on the ${campaign} Squad!\n\n` +
    `Can you find them all? Scan your next pack and see who YOU unveil! 🏆\n\n` +
    `#EveryBodyWins #${campaign.replace(/\s+/g, '')} #UnveilTheLegend`;
  return imageUrl ? `${base}\n\n${imageUrl}` : base;
};

const imageUrlToBlob = (url) =>
  new Promise((resolve, reject) => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('toBlob failed'))), 'image/png');
    };
    img.onerror = () => reject(new Error('Image load failed'));
    img.src = url;
  });

const fetchImageFile = async (url, filename) => {
  const blob = await imageUrlToBlob(url);
  return new File([blob], `${filename}.png`, { type: 'image/png' });
};

const platformLinks = (text, url) => {
  const fullText = text; // already contains imageUrl
  return [
    { key: 'whatsapp', label: 'WhatsApp', color: '#25D366', icon: '💬', href: `https://wa.me/?text=${encodeURIComponent(`${fullText}\n${url}`)}` },
    { key: 'twitter', label: 'X / Twitter', color: '#000000', icon: '𝕏', href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(fullText)}&url=${encodeURIComponent(url)}` },
    { key: 'facebook', label: 'Facebook', color: '#1877F2', icon: 'f', href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(fullText)}` },
    { key: 'telegram', label: 'Telegram', color: '#229ED9', icon: '✈️', href: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(fullText)}` }
  ];
};

const EveryBodyWins = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const mode = location.state?.mode || 'tiered';
  const teamImage = location.state?.teamImage || null;
  const players = useMemo(() => location.state?.players || [], [location.state?.players]);
  const participantName = location.state?.participantName || '';
  const campaignName = location.state?.campaignName || '';
  // Opened from the notice pop-up (the can): they haven't played, so there's nothing to play again
  const fromNotice = !!location.state?.fromNotice;

  const [revealed, setRevealed] = useState(mode === 'team' ? [false] : [false, false, false, false]);
  const [sharing, setSharing] = useState(null);
  const [shareModal, setShareModal] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (mode === 'team') {
      const timer = setTimeout(() => setRevealed([true]), 500);
      return () => clearTimeout(timer);
    }
    const timers = [0, 1, 2, 3].map((i) =>
      setTimeout(() => {
        setRevealed((prev) => {
          const next = [...prev];
          next[i] = true;
          return next;
        });
      }, 500 + i * 600)
    );
    return () => timers.forEach(clearTimeout);
  }, [mode]);

  const handleDownload = useCallback((idx) => {
    const player = mode === 'team' ? teamImage : players[idx];
    if (!player?.image) return;
    const filename = `${(player.name || `Player-${idx + 1}`).replace(/\s+/g, '-')}-${campaignName.replace(/\s+/g, '-') || 'squad'}`;
    downloadImage(player.image, filename);
  }, [players, campaignName, mode, teamImage]);

  const handleShare = useCallback(async (idx) => {
    const player = mode === 'team' ? teamImage : players[idx];
    if (!player?.image) return;
    setSharing(idx);

    const playerName = player.name || `Tier ${idx + 1} Player`;
    const imageUrl = player.image;
    const shareText = buildShareText(playerName, participantName, campaignName, imageUrl);
    const shareUrl = window.location.origin;

    try {
      if (navigator.canShare) {
        try {
          const file = await fetchImageFile(player.image, playerName.replace(/\s+/g, '-'));
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              files: [file],
              title: `${campaignName ? `${campaignName} — ` : ''}${playerName} Unveiled!`,
              text: shareText,
              url: shareUrl
            });
            setSharing(null);
            return;
          }
        } catch (err) {
          if (err.name === 'AbortError') { setSharing(null); return; }
        }
      }

      if (navigator.share) {
        try {
          await navigator.share({
            title: `${campaignName ? `${campaignName} — ` : ''}${playerName} Unveiled!`,
            text: shareText,
            url: shareUrl
          });
          setSharing(null);
          return;
        } catch (err) {
          if (err.name === 'AbortError') { setSharing(null); return; }
        }
      }

      setShareModal({ playerName, shareText, shareUrl, imageUrl });
    } finally {
      setSharing(null);
    }
  }, [players, participantName, campaignName, mode, teamImage]);

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  const handlePlayAgain = () => navigate('/', { replace: true });

  return (
    <>
      <Header title="Everybody Wins!" rightContent={' '} />
      <main className="page-content strict-width x-space-top">
        <div className="container pb-4">
          <div className="card white-card border-0 rounded-3 mb-4 mx-auto text-center">
            <div className="card-body text-center p-4">
              <h4 className="mb-2">{participantName ? `Congratulations! ${participantName}` : 'Congratulations!'}</h4>
              <p className="font-light" style={{ fontSize: '0.75em' }}>
                You've just unveiled four iconic players.
              </p>
            </div>
          </div>

          <EveryBodyWinsList
            mode={mode}
            teamImage={teamImage}
            players={players}
            revealed={revealed}
            sharing={sharing}
            onDownload={handleDownload}
            onShare={handleShare}
            onPlayAgain={fromNotice ? undefined : handlePlayAgain}
            shareModal={shareModal}
            onCloseShareModal={() => setShareModal(null)}
            platformLinks={shareModal ? platformLinks(shareModal.shareText, shareModal.shareUrl) : []}
            copied={copied}
            onCopy={handleCopy}
          />
        </div>
      </main>
    </>
  );
};

export default EveryBodyWins;
